import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { api, errMsg } from "../lib/api";
import { useApp, useModule } from "../context/AppContext";
import { PageHeader, Loading, ErrorBox, Denied, Btn, Pill, fmtDate } from "../components/common";
import { OptionSettings, LegislativeSettings } from "./AdminSettings";
import { usePersonnel } from "../components/GovFields";
import { TenantScope, AccountModal } from "../components/AdminScope";

const inputCls = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm";

function NewUser({ roles, open, onClose, onDone }) {
  const [f, setF] = useState({ email: "", name: "", role: Object.keys(roles)[0] || "", password: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post("/admin/users", f); toast.success("Account created"); onDone(); onClose(); } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-user-dialog">
        <DialogHeader><DialogTitle>Create user account</DialogTitle><DialogDescription>Role assignment is audited. Super Admin cannot be assigned.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="new-user-name" aria-label="Full name" />
          <input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="new-user-email" aria-label="Email" />
          <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={inputCls} data-testid="new-user-role" aria-label="Role">
            {Object.entries(roles).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input required type="password" placeholder="Temporary password (min 10 chars)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} data-testid="new-user-password" aria-label="Temporary password" />
          {err && <p className="text-sm text-red-700" data-testid="new-user-error">{err}</p>}
          <div className="flex justify-end gap-2"><Btn type="button" variant="outline" onClick={onClose}>Cancel</Btn><Btn type="submit" data-testid="new-user-submit">Create account</Btn></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Users() {
  const { meta } = useApp();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const people = usePersonnel();
  const load = useCallback(() => api.get("/admin/users").then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const patch = async (u, body) => { try { await api.patch(`/admin/users/${u.id}`, body); toast.success("User updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (err) return <ErrorBox text={err} />;
  if (!d) return <Loading />;
  return (
    <div>
      <div className="flex justify-end mb-3"><Btn onClick={() => setOpen(true)} data-testid="new-user-btn"><UserPlus className="h-4 w-4" />New user</Btn></div>
      <div className="overflow-x-auto rounded-md border bg-white" data-testid="users-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Linked personnel</th><th className="p-3">Status</th><th className="p-3">Last login</th></tr></thead>
          <tbody className="divide-y">
            {d.items.map((u) => {
              const locked = u.role === "super_admin" || u.id === meta.user.id;
              return (
                <tr key={u.id} data-testid={`user-row-${u.email}`}>
                  <td className="p-3 font-medium"><button onClick={() => api.get(`/admin/users/${u.id}`).then((r) => setSel(r.data)).catch((e) => toast.error(errMsg(e)))} className="text-left text-brand hover:underline" data-testid={`user-open-${u.email}`}>{u.name}</button></td>
                  <td className="p-3 text-slate-600">{u.email}</td>
                  <td className="p-3">
                    {locked ? <span className="text-xs">{u.role_label}</span> : (
                      <select value={u.role} onChange={(e) => patch(u, { role: e.target.value })} className="rounded border px-2 py-1 text-xs" data-testid={`user-role-select-${u.email}`} aria-label={`Role for ${u.email}`}>
                        {Object.entries(d.assignable_roles).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="p-3">
                    <select value={u.personnel_id || ""} onChange={(e) => patch(u, { personnel_id: e.target.value })} className="rounded border px-2 py-1 text-xs max-w-[12rem]" data-testid={`user-personnel-select-${u.email}`} aria-label={`Linked personnel for ${u.email}`}>
                      <option value="">— Not linked —</option>
                      {people.filter((p) => p.id === u.personnel_id || !d.items.some((x) => x.personnel_id === p.id)).map((p) => <option key={p.id} value={p.id}>{p.name} ({p.position})</option>)}
                    </select>
                  </td>
                  <td className="p-3">
                    {locked ? <Pill tone="approved">active</Pill> : (
                      <button onClick={() => patch(u, { is_active: !u.is_active })} data-testid={`user-toggle-${u.email}`}><Pill tone={u.is_active ? "approved" : "rejected"}>{u.is_active ? "active" : "disabled"}</Pill></button>
                    )}
                  </td>
                  <td className="p-3 text-xs text-slate-500">{fmtDate(u.last_login_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <NewUser roles={d.assignable_roles} open={open} onClose={() => setOpen(false)} onDone={load} />
      {sel && <AccountModal prefix="user" title={`Account: ${sel.name}`} detail={sel} onClose={() => setSel(null)}
        rows={[["Account ID", sel.id], ["Email", sel.email], ["Role", sel.role_label], ["Jurisdiction", sel.jurisdiction && [sel.jurisdiction.barangay, sel.jurisdiction.city, sel.jurisdiction.province].filter(Boolean).join(", ")], ["Linked personnel", sel.personnel], ["Status", sel.is_active ? "Active" : "Disabled"], ["Two-factor", sel.totp_enrolled ? "Authenticator app" : sel.otp_enabled ? "On" : "Off"], ["Created", sel.created_at], ["Last updated", sel.updated_at], ["Last login", sel.last_login_at]]}
        fields={[{ key: "name", label: "Full name" }, { key: "email", label: "Email", type: "email" }, ...(sel.id === meta.user.id ? [] : [{ key: "is_active", label: "Account status", type: "bool", options: [["true", "Active"], ["false", "Disabled"]] }])]}
        onSave={async (body) => { const r = await api.patch(`/admin/users/${sel.id}`, body); setSel(r.data); load(); }} />}
    </div>
  );
}

function Audit() {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ action: "", outcome: "", page: 1 });
  const [err, setErr] = useState("");
  useEffect(() => {
    let live = true;
    api.get("/admin/audit", { params: f }).then((r) => { if (live) setD(r.data); }).catch((e) => { if (live) setErr(errMsg(e)); });
    return () => { live = false; };
  }, [f]);
  if (err) return <ErrorBox text={err} />;
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        <input placeholder="Filter action (e.g. settings, login)" value={f.action} onChange={(e) => setF({ ...f, action: e.target.value, page: 1 })} className="rounded-md border px-3 py-2 text-sm w-64" data-testid="audit-action-filter" aria-label="Filter action" />
        <select value={f.outcome} onChange={(e) => setF({ ...f, outcome: e.target.value, page: 1 })} className="rounded-md border px-3 py-2 text-sm" data-testid="audit-outcome-filter" aria-label="Filter outcome">
          <option value="">All outcomes</option><option value="success">Success</option><option value="denied">Denied</option><option value="failure">Failure</option>
        </select>
      </div>
      {!d ? <Loading /> : (
        <>
          <div className="overflow-x-auto rounded-md border bg-white" data-testid="audit-table">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-left uppercase text-slate-500"><tr><th className="p-2">Time</th><th className="p-2">Actor</th><th className="p-2">Action</th><th className="p-2">Entity / Ref</th><th className="p-2">Outcome</th><th className="p-2">Details</th></tr></thead>
              <tbody className="divide-y">
                {d.items.map((e) => (
                  <tr key={e.id}>
                    <td className="p-2 whitespace-nowrap">{fmtDate(e.at)}</td>
                    <td className="p-2">{e.actor}<div className="text-slate-400">{e.role}</div></td>
                    <td className="p-2 font-mono">{e.action}</td>
                    <td className="p-2">{e.entity} <span className="font-mono text-slate-500">{e.ref}</span></td>
                    <td className="p-2"><Pill tone={e.outcome === "success" ? "approved" : "rejected"}>{e.outcome}</Pill></td>
                    <td className="p-2 max-w-xs truncate font-mono text-slate-500" title={JSON.stringify(e.details)}>{e.details ? JSON.stringify(e.details) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 flex justify-between text-xs text-slate-600">
            <span>{d.total} events (append-only)</span>
            <div className="flex gap-1"><Btn variant="outline" className="!py-1" disabled={f.page <= 1} onClick={() => setF({ ...f, page: f.page - 1 })} data-testid="audit-prev">Prev</Btn><Btn variant="outline" className="!py-1" disabled={f.page * d.size >= d.total} onClick={() => setF({ ...f, page: f.page + 1 })} data-testid="audit-next">Next</Btn></div>
          </div>
        </>
      )}
    </div>
  );
}

function Outbound() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/outbound").then((r) => setD(r.data)).catch(() => setD([])); }, []);
  if (!d) return <Loading />;
  return d.length === 0 ? <p className="text-sm text-slate-500">No outbound messages yet.</p> : (
    <div className="rounded-md border bg-white overflow-x-auto" data-testid="outbound-table">
      <table className="w-full text-xs"><thead className="bg-slate-50 text-left uppercase text-slate-500"><tr><th className="p-2">Time</th><th className="p-2">Channel</th><th className="p-2">Recipient</th><th className="p-2">Template</th><th className="p-2">Status</th><th className="p-2">Note</th></tr></thead>
        <tbody className="divide-y">{d.map((m) => <tr key={m.id}><td className="p-2">{fmtDate(m.at)}</td><td className="p-2">{m.channel}</td><td className="p-2">{m.recipient}</td><td className="p-2">{m.template}</td><td className="p-2"><Pill tone={m.status === "sent" ? "approved" : "escalated"}>{m.status}</Pill></td><td className="p-2">{m.error}</td></tr>)}</tbody>
      </table>
    </div>
  );
}

export default function PlatformAdmin() {
  const { meta } = useApp();
  const [scope, setScope] = useState(null);
  if (meta.user.role !== "super_admin") return <PlatformBody />;
  return (
    <div>
      <PageHeader crumbs={["Administration", "Platform"]} title="Central LGU & Platform Administration" subtitle="Select an activated barangay inside the deployment scope. Changes apply only to that barangay." />
      <TenantScope onChange={setScope} prefix="platform-scope" />
      {scope && <PlatformBody key={scope.id} embedded />}
    </div>
  );
}

function PlatformBody({ embedded }) {
  const allowed = useModule("platform");
  const { meta } = useApp();
  const [t, setT] = useState(null);
  useEffect(() => { if (allowed) api.get("/admin/tenant").then((r) => setT(r.data)).catch(() => {}); }, [allowed]);
  if (!allowed) return <Denied />;
  const isAdmin = ["super_admin", "sys_admin"].includes(meta.user.role);
  return (
    <div data-testid="platform-page">
      {!embedded && <PageHeader crumbs={["Administration", "Platform"]} title="Central LGU & Platform Administration" subtitle="User accounts and role assignment for this LGU, the append-only audit trail and the outbound delivery log. Branding and credentials live in System Settings (Super Admin only)." />}
      {t && <div className="grid sm:grid-cols-4 gap-3 mb-6" data-testid="tenant-info">
        {[["LGU", t.name], ["Jurisdiction", `${t.barangay}, ${t.city}, ${t.province}`], ["User accounts", t.users], ["Records", t.records]].map(([k, v]) => <div key={k} className="rounded-md border bg-white p-4"><p className="overline-label">{k}</p><p className="font-semibold mt-1 text-sm">{v}</p></div>)}
      </div>}
      <Tabs defaultValue={isAdmin ? "users" : "audit"}>
        <TabsList className="flex-wrap h-auto">
          {isAdmin && <TabsTrigger value="users" data-testid="tab-users">Users & roles</TabsTrigger>}
          {isAdmin && <TabsTrigger value="options" data-testid="tab-options">Option Settings</TabsTrigger>}
          {isAdmin && <TabsTrigger value="legislative" data-testid="tab-legislative">Legislative Settings</TabsTrigger>}
          <TabsTrigger value="audit" data-testid="tab-audit">Audit trail</TabsTrigger>
          {isAdmin && <TabsTrigger value="outbound" data-testid="tab-outbound">Outbound messages</TabsTrigger>}
        </TabsList>
        {isAdmin && <TabsContent value="users" className="mt-4"><Users /></TabsContent>}
        {isAdmin && <TabsContent value="options" className="mt-4"><OptionSettings /></TabsContent>}
        {isAdmin && <TabsContent value="legislative" className="mt-4"><LegislativeSettings /></TabsContent>}
        <TabsContent value="audit" className="mt-4"><Audit /></TabsContent>
        {isAdmin && <TabsContent value="outbound" className="mt-4"><Outbound /></TabsContent>}
      </Tabs>
    </div>
  );
}
